import {
  Component,
  computed,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ControlConfig } from '../../../models/api/reports/report';
import { ValidatorsEx } from '../../../validators/validators-ex';
import { CheckboxComponent } from '../../core/controls/checkbox.component';
import { InputComponent } from '../../core/controls/input.component';
import { RangeComponent } from '../../core/controls/range.component';
import { SelectFieldComponent } from '../../core/fields/select-field.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { BaseCriteriaComponent } from './base-criteria.component';

interface FinancialGroup {
  index: number;
  erfNames: {
    customField: string;
    operator: string;
    begin: string;
    end: string;
  };
  formControlNames: {
    customField: string;
    operator: string;
    begin: string;
    end: string;
  };
  showCustomField: WritableSignal<boolean>;
  showOperator: WritableSignal<boolean>;
  showBeginRaw: WritableSignal<boolean>;
  showEndRaw: WritableSignal<boolean>;
  operatorValue: WritableSignal<string | null>;
  showBegin: Signal<boolean>;
  showEnd: Signal<boolean>;
  customFieldOptions: Signal<{ name: string; code: string }[]>;
  operatorOptions: Signal<{ name: string; code: string }[]>;
}

@Component({
  selector: 'app-financial-criteria',
  imports: [
    InputComponent,
    SelectFieldComponent,
    CheckboxComponent,
    PageHeaderComponent,
    RangeComponent,
  ],
  template: `
    <app-page-header label="Financial criteria" headerSize="h3" />

    @for (group of financialGroups; track group.index) {
      @if (group.showCustomField()) {
        <div class="tw-mb-6 tw-flex tw-items-start tw-gap-4">
          <app-select-field
            [label]="getLabel(group.erfNames.customField)"
            optionLabel="name"
            optionValue="code"
            [virtualScroll]="false"
            [showHeader]="false"
            [filter]="true"
            [multiple]="false"
            [showClear]="false"
            [options]="group.customFieldOptions()"
            [control]="fg.controls[group.formControlNames.customField]"
            valueClasses="tw-w-[220px] tw-min-w-[220px] tw-max-w-[220px]"
            labelClasses="tw-whitespace-nowrap"
          />
          @if (group.showOperator()) {
            <app-select-field
              optionLabel="name"
              optionValue="code"
              [virtualScroll]="false"
              [showHeader]="false"
              [filter]="false"
              [multiple]="false"
              [showClear]="true"
              [options]="group.operatorOptions()"
              [control]="fg.controls[group.formControlNames.operator]"
              valueClasses="tw-w-[250px] tw-min-w-[250px] tw-max-w-[250px]"
              [placeholder]="getLabel(group.erfNames.operator)"
            />
          }
          @if (group.showEnd()) {
            <app-range
              class="tw--mt-2"
              type="Currency"
              [control]="fg.controls[group.formControlNames.begin]"
              [toControl]="fg.controls[group.formControlNames.end]"
            />
          } @else if (group.showBegin()) {
            <app-input
              [control]="fg.controls[group.formControlNames.begin]"
              type="Currency"
              label="Amount"
              placeholder="Amount"
              rightIcon="fa-light fa-dollar-sign"
              screenReaderLabel="Amount"
            />
          }
        </div>
      }
    }

    <!-- Non-Financial Group -->
    @if (showNonFinancialField()) {
      <div class="tw-mb-4">
        <app-select-field
          [label]="getLabel('NON_FINANCIAL_FIELD')"
          optionLabel="name"
          optionValue="code"
          [virtualScroll]="false"
          [showHeader]="false"
          [filter]="true"
          [multiple]="false"
          [showClear]="false"
          [options]="nonFinancialFieldOptions()"
          [control]="fg.controls['nonFinancialField']"
          valueClasses="tw-max-w-[320px]"
        />
      </div>
    }
    @if (showNonFinancialDelta()) {
      <div class="tw-mb-6">
        <app-checkbox
          [label]="getLabel('NON_FINANCIAL_DELTA')"
          [control]="fg.controls['nonFinancialDelta']"
        />
      </div>
    }
  `,
})
export class FinancialCriteriaComponent extends BaseCriteriaComponent {
  financialGroups: FinancialGroup[] = [1, 2, 3].map((i) => {
    const showCustomField = signal(false);
    const showOperator = signal(false);
    const showBeginRaw = signal(false);
    const showEndRaw = signal(false);
    const operatorValue = signal<string | null>(null);

    return {
      index: i,
      erfNames: {
        customField: `FIN_CUSTOM_FIELD_${i}`,
        operator: `FINANCE_OPERATOR_${i}`,
        begin: `FINANCE_OPERATOR_BEGIN_${i}`,
        end: `FINANCE_OPERATOR_END_${i}`,
      },
      formControlNames: {
        customField: `finCustomField${i}`,
        operator: `financeOperator${i}`,
        begin: `financeOperatorBegin${i}`,
        end: `financeOperatorEnd${i}`,
      },
      showCustomField,
      showOperator,
      showBeginRaw,
      showEndRaw,
      operatorValue,
      showBegin: computed(() => this.shouldShowBegin(operatorValue())),
      showEnd: computed(() => this.shouldShowEnd(operatorValue())),
      customFieldOptions: computed(() =>
        this.controlConfig
          .getControlOptions(`FIN_CUSTOM_FIELD_${i}`)
          .sort((a, b) => a.name.localeCompare(b.name)),
      ),
      operatorOptions: computed(() =>
        this.controlConfig
          .getControlOptions(`FINANCE_OPERATOR_${i}`)
          .filter((opt) => opt.name && opt.name.trim().length > 0)
          .sort((a, b) => a.name.localeCompare(b.name)),
      ),
    };
  });

  showNonFinancialField = signal(false);
  showNonFinancialDelta = signal(false);

  nonFinancialFieldOptions = computed(() =>
    this.controlConfig
      .getControlOptions('NON_FINANCIAL_FIELD')
      .sort((a, b) => a.name.localeCompare(b.name)),
  );

  fg = new FormGroup<Record<string, FormControl>>({});

  protected readonly regionName = 'Selection Type';

  protected CONTROL_CONFIGS: ControlConfig[] = [
    ...this.financialGroups.flatMap((g) => [
      {
        erfNames: g.erfNames.customField,
        formControlName: g.formControlNames.customField,
        visibility: g.showCustomField,
        defaultToFirstOption: true,
        required: false,
      },
      {
        erfNames: g.erfNames.operator,
        formControlName: g.formControlNames.operator,
        visibility: g.showOperator,
        defaultToFirstOption: false,
        required: false, //override required to false to match classic
      },
      {
        erfNames: g.erfNames.begin,
        formControlName: g.formControlNames.begin,
        visibility: g.showBeginRaw,
        type: 'number' as const,
        required: false,
      },
      {
        erfNames: g.erfNames.end,
        formControlName: g.formControlNames.end,
        visibility: g.showEndRaw,
        type: 'number' as const,
        required: false,
      },
    ]),
    {
      erfNames: 'NON_FINANCIAL_FIELD',
      formControlName: 'nonFinancialField',
      visibility: this.showNonFinancialField,
      defaultToFirstOption: true,
      required: false,
    },
    {
      erfNames: 'NON_FINANCIAL_DELTA',
      formControlName: 'nonFinancialDelta',
      visibility: this.showNonFinancialDelta,
      transformValue: (value: any) =>
        value === 'Y' || value === 'TRUE' || value === true ? true : false,
    },
  ];

  override ngOnInit(): void {
    super.ngOnInit();
    this.syncOperatorState();
  }

  override loadAllConfigurations(): void {
    super.loadAllConfigurations();
    this.syncOperatorState();
  }

  override reloadFormValues(): void {
    super.reloadFormValues();
    this.syncOperatorState(true);
  }

  protected override setupFormListeners(): void {
    for (const group of this.financialGroups) {
      const operatorCtrl = this.fg.get(
        group.formControlNames.operator,
      ) as FormControl | null;
      const beginCtrl = this.fg.get(
        group.formControlNames.begin,
      ) as FormControl | null;
      const endCtrl = this.fg.get(
        group.formControlNames.end,
      ) as FormControl | null;

      if (operatorCtrl) {
        let previousOperatorValue = operatorCtrl.value?.toLowerCase() ?? null;
        this.dispose.subs = operatorCtrl.valueChanges.subscribe((value) => {
          const normalizedValue = value?.toLowerCase() ?? null;
          group.operatorValue.set(normalizedValue);

          if (normalizedValue !== previousOperatorValue) {
            beginCtrl?.setValue(null, { emitEvent: false });
            endCtrl?.setValue(null, { emitEvent: false });
          }
          previousOperatorValue = normalizedValue;

          this.updateAmountValidators(normalizedValue, beginCtrl, endCtrl);
        });
      }

      this.setupAmountCrossValidation(
        beginCtrl,
        endCtrl,
        () => operatorCtrl?.value,
      );
    }
  }

  private updateAmountValidators(
    operator: string | null | undefined,
    beginControl: FormControl | null | undefined,
    endControl: FormControl | null | undefined,
  ): void {
    if (!beginControl || !endControl) return;

    beginControl.clearValidators();
    endControl.clearValidators();

    const op = operator?.toLowerCase();
    if (op && op !== 'none') {
      beginControl.addValidators(Validators.required);

      if (op === 'between' || op === 'not_between') {
        endControl.addValidators(Validators.required);
        beginControl.addValidators(ValidatorsEx.currencyLessThan(endControl));
        endControl.addValidators(
          ValidatorsEx.currencyGreaterThan(beginControl),
        );
        beginControl.addValidators(ValidatorsEx.currencyRange(beginControl));
        endControl.addValidators(ValidatorsEx.currencyRange(endControl));
      }
    }

    beginControl.updateValueAndValidity();
    endControl.updateValueAndValidity();
  }

  private syncOperatorState(showValidationErrors = false): void {
    for (const group of this.financialGroups) {
      const operatorCtrl = this.fg.get(
        group.formControlNames.operator,
      ) as FormControl | null;
      const beginCtrl = this.fg.get(
        group.formControlNames.begin,
      ) as FormControl | null;
      const endCtrl = this.fg.get(
        group.formControlNames.end,
      ) as FormControl | null;

      const op = operatorCtrl?.value?.toLowerCase();
      const normalized = op && op !== 'none' ? op : 'none';

      group.operatorValue.set(normalized);

      if (operatorCtrl?.value === 'none') {
        operatorCtrl.setValue(null, { emitEvent: false });
      }

      this.updateAmountValidators(
        normalized !== 'none' ? normalized : null,
        beginCtrl,
        endCtrl,
      );

      if (showValidationErrors) {
        this.markInvalidAmountControls(beginCtrl, endCtrl);
      }
    }
  }

  private markInvalidAmountControls(
    beginControl: FormControl | null | undefined,
    endControl: FormControl | null | undefined,
  ): void {
    if (beginControl?.invalid) {
      beginControl.markAsDirty();
      beginControl.markAsTouched();
    }
    if (endControl?.invalid) {
      endControl.markAsDirty();
      endControl.markAsTouched();
    }
  }

  private isUpdatingCrossValidation = false;

  private setupAmountCrossValidation(
    beginControl: FormControl | null | undefined,
    endControl: FormControl | null | undefined,
    getOperator: () => string | null,
  ): void {
    if (!beginControl || !endControl) return;

    this.dispose.subs = beginControl.valueChanges.subscribe(() => {
      if (this.isUpdatingCrossValidation) return;
      beginControl.markAsDirty();
      beginControl.markAsTouched();
      const op = getOperator()?.toLowerCase();

      if (op === 'between' || op === 'not_between') {
        this.isUpdatingCrossValidation = true;
        if (!endControl.dirty) {
          endControl.markAsDirty();
        }
        if (!endControl.touched) {
          endControl.markAsTouched();
        }
        endControl.updateValueAndValidity();
        this.isUpdatingCrossValidation = false;
      }
    });

    this.dispose.subs = endControl.valueChanges.subscribe(() => {
      if (this.isUpdatingCrossValidation) return;
      endControl.markAsDirty();
      endControl.markAsTouched();
      const op = getOperator()?.toLowerCase();

      if (op === 'between' || op === 'not_between') {
        this.isUpdatingCrossValidation = true;
        if (!beginControl.dirty) {
          beginControl.markAsDirty();
        }
        if (!beginControl.touched) {
          beginControl.markAsTouched();
        }
        beginControl.updateValueAndValidity();
        this.isUpdatingCrossValidation = false;
      }
    });
  }

  private shouldShowBegin(operator: string | null): boolean {
    const op = operator?.toLowerCase();
    return op !== null && op !== undefined && op !== 'none';
  }

  private shouldShowEnd(operator: string | null): boolean {
    const op = operator?.toLowerCase();
    return op === 'between' || op === 'not_between';
  }

  protected getLabel(erfName: string): string | undefined {
    return this.controlConfig.getControlLabel(erfName);
  }
}
